'use client';

import { useEffect, RefObject } from 'react';

type DismissOptions = {
  /** Listen in the capture phase for more reliable outside clicks. Default: true */
  capture?: boolean;
  /** Close on Escape key. Default: true */
  closeOnEscape?: boolean;
  /** Close on outside pointer down. Default: true */
  closeOnOutsidePointer?: boolean;
};

export function useDismiss(
  enabled: boolean,
  ref: RefObject<HTMLElement | null>,
  onClose: () => void,
  {
    capture = true,
    closeOnEscape = true,
    closeOnOutsidePointer = true,
  }: DismissOptions = {}
) {
  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (!closeOnEscape) return;
      if (e.key === 'Escape') onClose();
    };

    const onPointerDown = (e: PointerEvent | MouseEvent) => {
      if (!closeOnOutsidePointer) return;
      if (!ref.current) return;
      const el = ref.current;
      const target = e.target as Node | null;
      // Only close if clicking outside the referenced element
      if (el && target && !el.contains(target)) onClose();
    };

    // Use AddEventListenerOptions to keep types strict
    const options: AddEventListenerOptions = { capture };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown, options);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown, options);
    };
  }, [enabled, ref, onClose, capture, closeOnEscape, closeOnOutsidePointer]);
}